/* eslint-disable @atlaskit/design-system/no-emotion-primitives */
/* eslint-disable @repo/internal/react/require-jsdoc */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled
import { type Interpolation, jsx, type Theme } from '@emotion/react';
import upperFirst from 'lodash/upperFirst';

import { Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import type { TransformedTokenGrouped } from '../types';

import type { TokenValue } from './token-definition';

type TypographyTokenCategoryProps = {
	tokenOriginal: TransformedTokenGrouped;
	tokenValue: TokenValue;
};

const typographyTokenNameMap = {
	xxlarge: 'XXL',
	xlarge: 'XL',
	large: 'L',
	medium: 'M',
	small: 'S',
	xsmall: 'XS',
	xxsmall: 'XXS',
};

const getPreviewText = (tokenOriginal: TransformedTokenGrouped) => {
	let previewText = upperFirst(
		tokenOriginal.cleanName
			.replace('font.', '')
			.replace('heading.', 'Heading ')
			.replace('body.', 'Body ')
			.replace('metric.', 'Metric ')
			.replace('weight.', '')
			.replace('family.', ''),
	);

	switch (tokenOriginal.attributes.group) {
		case 'typography':
			const size = previewText.split(' ')[1] as keyof typeof typographyTokenNameMap;
			return previewText.replace(size, typographyTokenNameMap[size]);
		case 'fontWeight':
			return previewText;
		case 'fontFamily':
			return upperFirst(tokenOriginal.path[tokenOriginal.path.length - 1]);
	}
};

const getFontStyles = (
	tokenValue: TokenValue,
	tokenOriginal: TransformedTokenGrouped,
): Interpolation<Theme> => {
	const lastPath = tokenOriginal.path[tokenOriginal.path.length - 1];
	switch (tokenOriginal.attributes.group) {
		case 'typography':
			return {
				// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
				font: tokenValue.value,
				whiteSpace: 'pre-line',
			};
		case 'fontWeight':
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			return { fontWeight: tokenOriginal.value };
		case 'fontFamily':
			return {
				font: lastPath === 'heading' ? token('font.heading.large') : token('font.body'),
				fontWeight:
					lastPath === 'heading' ? token('font.weight.bold') : token('font.weight.regular'),
				// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
				fontFamily: tokenOriginal.value,
			};
	}
	return {};
};

export const Font = ({
	tokenValue,
	tokenOriginal,
}: TypographyTokenCategoryProps): React.JSX.Element => {
	const styles = getFontStyles(tokenValue, tokenOriginal);
	const previewText = getPreviewText(tokenOriginal);

	return (
		<Box>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */}
			<p css={styles}>{previewText}</p>
		</Box>
	);
};
